import type { Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { withFrappeLoading } from "@/services/frappe/list";
import { Controllers, FrappeMethodType } from "@/type/api";
import type {
  NotificationSettings,
  PushNotificationSettings,
  PushNotificationSettingsPayload,
  PushOneSignalStatus,
  PushRuntimeConfig,
  GetOneSignalStatusParams,
  SendTestPushPayload,
  PushSubscriptionPayload,
} from "@/type/pushNotifications";

const SETTINGS_DOCTYPE = "Pet App Notification Settings";
const pushService = new HttpClient(Controllers.pushNotificationSettings);

const GET_CONFIG_METHOD = "pet_app.api.push.get_config";
const REGISTER_SUBSCRIPTION_METHOD = "pet_app.api.push.register_subscription";
const UNREGISTER_SUBSCRIPTION_METHOD = "pet_app.api.push.unregister_subscription";
const GET_ONESIGNAL_STATUS_METHOD = "pet_app.api.push.get_onesignal_status";
const SEND_TEST_PUSH_METHOD = "pet_app.api.push.send_test_push";
const GET_NOTIFICATION_SETTINGS_METHOD =
  "pet_app.api.notifications.get_notification_settings";
const UPDATE_NOTIFICATION_SETTINGS_METHOD =
  "pet_app.api.notifications.update_notification_settings";
const ROOT_WORKER_PATH = "OneSignalSDKWorker.js";
const LEGACY_WORKER_PATH = "onesignal/OneSignalSDKWorker.js";

function unwrapMethodPayload<T>(response: any): T {
  return (response?.message ?? response?.data ?? response ?? null) as T;
}

function toBool(value: unknown): boolean {
  if (typeof value === "boolean")
    return value;

  return Number(value ?? 0) === 1;
}

function normalizeString(value: unknown): string | null {
  const normalized = String(value ?? "").trim();

  return normalized || null;
}

function normalizeWorkerPath(value: unknown): string {
  const normalized = normalizeString(value)?.replace(/^\/+/, "");

  return !normalized || normalized === LEGACY_WORKER_PATH
    ? ROOT_WORKER_PATH
    : normalized;
}

function mapRuntimeConfig(payload: any): PushRuntimeConfig {
  return {
    enabled: toBool(payload?.enabled),
    webEnabled: toBool(payload?.web_enabled),
    mobileEnabled: toBool(payload?.mobile_enabled),
    appId: normalizeString(payload?.app_id),
    externalId: normalizeString(payload?.external_id),
    serviceWorkerPath: normalizeWorkerPath(payload?.service_worker_path),
    serviceWorkerScope: normalizeString(payload?.service_worker_scope)
      ?? "/onesignal/",
  };
}

function mapOneSignalStatus(payload: any): PushOneSignalStatus {
  return {
    enabled: toBool(payload?.enabled),
    tokenPresent: toBool(payload?.token_present),
    externalId: normalizeString(
      payload?.external_id ?? payload?.externalId ?? payload?.user,
    ),
    oneSignalId: normalizeString(
      payload?.onesignal_id ?? payload?.oneSignalId,
    ),
    subscriptionId: normalizeString(
      payload?.subscription_id ?? payload?.subscriptionId,
    ),
  };
}

function mapSettings(payload: any): PushNotificationSettings {
  const apiKeyMarker = normalizeString(payload?.onesignal_rest_api_key);

  return {
    enabled: toBool(payload?.enabled),
    dryRun: toBool(payload?.dry_run),
    oneSignalEnabled: toBool(payload?.onesignal_enabled),
    webEnabled: toBool(payload?.onesignal_web_enabled),
    mobileEnabled: toBool(payload?.onesignal_mobile_enabled),
    mirrorSystemNotifications: toBool(
      payload?.onesignal_mirror_frappe_notifications,
    ),
    appId: normalizeString(payload?.onesignal_app_id),
    serviceWorkerPath: normalizeWorkerPath(
      payload?.onesignal_service_worker_path,
    ),
    serviceWorkerScope: normalizeString(payload?.onesignal_service_worker_scope)
      ?? "/onesignal/",
    // Password values are never exposed to the UI. A non-empty backend marker
    // only tells the form that a key is already stored.
    restApiKeyConfigured: Boolean(apiKeyMarker),
    pushFrontendBaseUrl: null,
  };
}

async function requestSettingsDoc() {
  const getWithName = () =>
    pushService.get<any>(
      "frappe.client.get",
      {},
      {
        doctype: SETTINGS_DOCTYPE,
        name: SETTINGS_DOCTYPE,
      },
      undefined,
      undefined,
      FrappeMethodType.method,
    );

  try {
    return await getWithName();
  }
  catch {
    return pushService.get<any>(
      "frappe.client.get",
      {},
      { doctype: SETTINGS_DOCTYPE },
      undefined,
      undefined,
      FrappeMethodType.method,
    );
  }
}

async function requestNotificationSettings(): Promise<NotificationSettings> {
  const response = await pushService.get<any>(
    GET_NOTIFICATION_SETTINGS_METHOD,
    {},
    {},
    undefined,
    undefined,
    FrappeMethodType.method,
  );
  const payload = unwrapMethodPayload<any>(response);

  return (payload?.settings ?? payload ?? {}) as NotificationSettings;
}

function buildSettingsFields(
  payload: PushNotificationSettingsPayload,
): Record<string, unknown> {
  const fields: Record<string, unknown> = {
    enabled: payload.enabled ? 1 : 0,
    dry_run: payload.dryRun ? 1 : 0,
    onesignal_enabled: payload.oneSignalEnabled ? 1 : 0,
    onesignal_web_enabled: payload.webEnabled ? 1 : 0,
    onesignal_mobile_enabled: payload.mobileEnabled ? 1 : 0,
    onesignal_mirror_frappe_notifications:
      payload.mirrorSystemNotifications ? 1 : 0,
    onesignal_app_id: payload.appId?.trim() ?? "",
    onesignal_service_worker_path: payload.serviceWorkerPath.trim(),
    onesignal_service_worker_scope: payload.serviceWorkerScope.trim(),
  };
  const apiKey = payload.restApiKey?.trim();

  // Blank means "leave the stored password unchanged". Never send a masked
  // placeholder back to the backend.
  if (apiKey)
    fields.onesignal_rest_api_key = apiKey;

  return fields;
}

export async function GetPushRuntimeConfig(): Promise<PushRuntimeConfig> {
  const response = await pushService.get<any>(
    GET_CONFIG_METHOD,
    {},
    {},
    undefined,
    undefined,
    FrappeMethodType.method,
  );

  return mapRuntimeConfig(unwrapMethodPayload(response));
}

export async function RegisterPushSubscription(
  payload: PushSubscriptionPayload,
): Promise<void> {
  await pushService.post<any>(
    {
      subscription_id: payload.subscriptionId,
      onesignal_id: payload.oneSignalId,
      platform: payload.platform,
      device_id: payload.deviceId,
      permission: payload.permission,
      token: payload.token,
      opted_in: payload.optedIn,
    },
    REGISTER_SUBSCRIPTION_METHOD,
    {},
    undefined,
    FrappeMethodType.method,
  );
}

export async function UnregisterPushSubscription(
  subscriptionId: string,
): Promise<void> {
  await pushService.post<any>(
    { subscription_id: subscriptionId },
    UNREGISTER_SUBSCRIPTION_METHOD,
    {},
    undefined,
    FrappeMethodType.method,
  );
}

export async function GetOneSignalStatus(
  payload: GetOneSignalStatusParams,
): Promise<PushOneSignalStatus> {
  const response = await pushService.get<any>(
    GET_ONESIGNAL_STATUS_METHOD,
    {},
    {
      user: payload.user,
      subscription_id: payload.subscriptionId,
    },
    undefined,
    undefined,
    FrappeMethodType.method,
  );
  const status = mapOneSignalStatus(unwrapMethodPayload(response));

  // The endpoint is an exact-device lookup. Refuse a response for another
  // registration so Profile can never display or target a sibling browser.
  if (
    status.subscriptionId !== payload.subscriptionId
    || (
      status.externalId !== null
      && status.externalId !== payload.user
    )
  ) {
    throw new Error("Push status did not match the requested device");
  }

  return status;
}

export async function SendTestPush(
  payload: SendTestPushPayload,
): Promise<void> {
  await pushService.post<any>(
    {
      user: payload.user,
      title: payload.title,
      body: payload.body,
      subscription_id: payload.subscriptionId,
    },
    SEND_TEST_PUSH_METHOD,
    {},
    undefined,
    FrappeMethodType.method,
  );
}

export async function GetPushNotificationSettings(
  loading: Ref<boolean>,
): Promise<PushNotificationSettings> {
  return withFrappeLoading(loading, async () => {
    const [settingsResponse, notificationSettings] = await Promise.all([
      requestSettingsDoc(),
      requestNotificationSettings(),
    ]);

    return {
      ...mapSettings(unwrapMethodPayload(settingsResponse)),
      pushFrontendBaseUrl: normalizeString(
        notificationSettings.push_frontend_base_url,
      ),
    };
  });
}

export async function UpdatePushNotificationSettings(
  loading: Ref<boolean>,
  payload: PushNotificationSettingsPayload,
): Promise<PushNotificationSettings> {
  return withFrappeLoading(loading, async () => {
    await pushService.post<any>(
      {
        doctype: SETTINGS_DOCTYPE,
        name: SETTINGS_DOCTYPE,
        fieldname: JSON.stringify(buildSettingsFields(payload)),
      },
      "frappe.client.set_value",
      {},
      undefined,
      FrappeMethodType.method,
    );

    await pushService.post<any>(
      {
        push_frontend_base_url: payload.pushFrontendBaseUrl?.trim() || "",
      },
      UPDATE_NOTIFICATION_SETTINGS_METHOD,
      {},
      undefined,
      FrappeMethodType.method,
    );

    const [settingsResponse, notificationSettings] = await Promise.all([
      requestSettingsDoc(),
      requestNotificationSettings(),
    ]);

    return {
      ...mapSettings(unwrapMethodPayload(settingsResponse)),
      pushFrontendBaseUrl: normalizeString(
        notificationSettings.push_frontend_base_url,
      ),
    };
  });
}
